import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { useDispatch } from 'react-redux';

import { ClaimRewardApiResponse } from '../dailyRewards.models';

import { addErrorMessageAction } from 'actions/global-actions';

import { claimDailyReward } from '../dailyRewards.service';

import { queryKeys } from 'hooks/queries/query-keys';

const DEFAULT_COOLDOWN_SECONDS = 10;
const THROTTLE_MESSAGE = 'Too many claim attempts, please wait a moment.';

type ThrottleResponse = { detail?: string };
type ClaimErrorResponse = ClaimRewardApiResponse | ThrottleResponse;

const parseRetryAfterSeconds = (detail: string | undefined): number | null => {
  if (!detail) return null;
  const match = /(\d+)\s*seconds?/i.exec(detail);
  if (!match) return null;
  const seconds = Number(match[1]);
  return Number.isFinite(seconds) && seconds > 0 ? seconds : null;
};

export const useClaimDailyReward = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const [cooldownUntil, setCooldownUntil] = useState<number | null>(null);

  const mutation = useMutation<ClaimRewardApiResponse, AxiosError<ClaimErrorResponse>>({
    mutationFn: claimDailyReward,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dailyRewards.all });
      // A claim can credit VIP Points — it is one of the prize kinds, and the VIP
      // multiplier scales it — so the balance behind this modal moves. Without this,
      // a holder claiming while the VIP page is mounted watches their points sit at
      // the pre-claim figure and concludes the reward did not arrive.
      queryClient.invalidateQueries({ queryKey: queryKeys.vipUser.all });
    },
    onError: (error) => {
      if (error.response?.status === 429) {
        const detail = (error.response.data as ThrottleResponse | undefined)?.detail;
        const retryAfter = parseRetryAfterSeconds(detail) ?? DEFAULT_COOLDOWN_SECONDS;
        setCooldownUntil(Date.now() + retryAfter * 1000);
        dispatch(addErrorMessageAction(THROTTLE_MESSAGE));
        return;
      }
      const data = error.response?.data as ClaimRewardApiResponse | undefined;
      const message = data?.message || 'Something went wrong. Please try again';
      dispatch(addErrorMessageAction(message));
    },
  });

  return {
    ...mutation,
    cooldownUntil,
    clearCooldown: () => setCooldownUntil(null),
  };
};
